/**
 * /kiosk — Fullscreen, touch-friendly kiosk UI.
 *
 * This page is loaded in Firefox-kiosk-mode on the physical kiosk PC. It does
 * NOT use the normal player session (no AuthContext, no cookies) — instead it
 * authenticates each customer with phone + 4-digit PIN against the kiosk
 * backend, then drives the bill acceptor.
 *
 * Phase 1 (right now): bill events are SIMULATED via on-screen buttons so the
 * entire flow can be tested without any hardware. Phase 3 will replace the
 * simulator buttons with a WebSocket to ws://localhost:9999 (the native
 * Genmega helper).
 *
 * The kiosk's `device_token` must be set in localStorage under the key
 * `kiosk_device_token` (we expose a one-time setup screen for that — visit
 * /kiosk?setup=1 to configure).
 */

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Phone, KeyRound, Banknote, CheckCircle2, ArrowLeft, LogOut,
    Loader2, AlertTriangle, DollarSign, Settings, Wifi, WifiOff,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const TOKEN_KEY = 'kiosk_device_token';
const SESSION_IDLE_MS = 60_000;          // 60 sec idle → auto-logout
const BILLS = [1, 5, 10, 20, 50, 100];   // simulator bill denominations
const HELPER_WS_URL = 'ws://127.0.0.1:9999';  // local kiosk hardware helper

// Generate a UUID — used as the per-deposit idempotency key
const uuid = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

const KioskPage = () => {
    const [searchParams] = useSearchParams();
    const [deviceToken, setDeviceToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
    const [setupInput, setSetupInput] = useState('');
    const [stage, setStage] = useState('idle');   // idle | login | menu | loading-cash | success | error
    const [phone, setPhone] = useState('');
    const [pin, setPin] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [player, setPlayer] = useState(null);   // { user_id, name, credits, ... }
    const [pendingCash, setPendingCash] = useState(0);
    const [lastDeposit, setLastDeposit] = useState(null);
    // Hardware helper status — when null, no helper has ever connected (simulator mode).
    // When set, the page uses real bill events from the local Python service.
    const [helperStatus, setHelperStatus] = useState(null);
    const idleTimerRef = useRef(null);
    const wsRef = useRef(null);
    const setupMode = searchParams.get('setup') === '1';

    // ────────────────────────── Idle-timeout: auto-logout the customer ──────────────────────────
    const bumpIdle = () => {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        if (stage === 'idle') return;
        idleTimerRef.current = setTimeout(() => {
            if (pendingCash > 0) {
                // SAFETY: if there's uncommitted cash and the customer walks away,
                // auto-submit it instead of leaving it in limbo. They've already
                // physically inserted the bills — the kiosk owes them the credit.
                submitDeposit('idle-timeout');
            } else {
                resetSession();
            }
        }, SESSION_IDLE_MS);
    };

    useEffect(() => {
        bumpIdle();
        return () => idleTimerRef.current && clearTimeout(idleTimerRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stage, pendingCash]);

    // ────────────────────────── Hardware helper (ws://localhost:9999) ──────────────────────────
    // Tries to connect to the local Python helper. If the helper isn't installed
    // or running, we silently fall back to the on-screen simulator buttons.
    // Reconnects with exponential backoff so a helper restart auto-recovers.
    useEffect(() => {
        let cancelled = false;
        let backoff = 1000;
        let timer = null;

        const connect = () => {
            if (cancelled) return;
            let ws;
            try {
                ws = new WebSocket(HELPER_WS_URL);
            } catch {
                timer = setTimeout(connect, backoff = Math.min(backoff * 2, 15_000));
                return;
            }
            wsRef.current = ws;

            ws.onopen = () => {
                if (cancelled) return;
                backoff = 1000;
                // eslint-disable-next-line no-console
                console.log('[kiosk-helper] connected');
            };
            ws.onmessage = (e) => {
                if (cancelled) return;
                let msg;
                try { msg = JSON.parse(e.data); } catch { return; }
                if (msg.event === 'status') {
                    setHelperStatus({ acceptor: msg.acceptor, printer: msg.printer });
                } else if (msg.event === 'bill') {
                    // Real bill from the hardware — add it to the running total.
                    // eslint-disable-next-line no-console
                    console.log(`[kiosk-helper] bill +$${msg.denom}`);
                    setPendingCash((c) => c + Number(msg.denom || 0));
                    bumpIdle();
                } else if (msg.event === 'error') {
                    setError(msg.msg || 'Hardware error — see staff.');
                }
            };
            ws.onclose = () => {
                if (cancelled) return;
                wsRef.current = null;
                // Don't blank helperStatus immediately — if the helper restarts
                // we want to keep showing "online" briefly to avoid UI flicker.
                timer = setTimeout(connect, backoff = Math.min(backoff * 2, 15_000));
            };
            ws.onerror = () => { /* onclose will fire next */ };
        };

        connect();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
            if (wsRef.current) {
                try { wsRef.current.close(); } catch { /* ignore */ }
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Tell the helper to enable / disable the bill acceptor based on UI stage.
    // We ONLY want to accept bills while the customer is actively on the
    // loading-cash screen — otherwise a passerby could drop bills in.
    useEffect(() => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        try {
            ws.send(JSON.stringify({ cmd: stage === 'loading-cash' ? 'enable' : 'disable' }));
        } catch { /* ignore */ }
    }, [stage]);

    // Best-effort helper command: fire & forget. Used to print receipts.
    const helperSend = (msg) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        try { ws.send(JSON.stringify(msg)); } catch { /* ignore */ }
    };

    // ────────────────────────── Setup screen (one-time device token entry) ──────────────────────────
    if (setupMode || !deviceToken) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
                <div className="card-dark p-8 max-w-md w-full">
                    <div className="flex items-center gap-3 mb-6">
                        <Settings className="w-7 h-7 text-[#FFD700]" />
                        <h1 className="text-2xl font-black text-white">Kiosk Setup</h1>
                    </div>
                    <p className="text-[#A0AAB2] text-sm mb-4">
                        Paste the <span className="text-white font-mono">device_token</span> issued from the BETohalics admin panel
                        ({setupMode ? 'Admin → Kiosks' : 'this kiosk is not configured yet'}). It is stored on this device only.
                    </p>
                    <textarea
                        value={setupInput}
                        onChange={(e) => setSetupInput(e.target.value.trim())}
                        rows={3}
                        placeholder="paste device_token here"
                        className="w-full p-3 rounded-lg bg-[#0a0a0a] border border-white/10 text-white font-mono text-xs"
                        data-testid="kiosk-setup-token-input"
                    />
                    <Button
                        onClick={() => {
                            if (!setupInput || setupInput.length < 16) {
                                alert('That does not look like a valid token.');
                                return;
                            }
                            localStorage.setItem(TOKEN_KEY, setupInput);
                            setDeviceToken(setupInput);
                            window.location.assign('/kiosk');
                        }}
                        className="w-full mt-4 bg-[#FFD700] text-black hover:bg-[#F4C430] font-bold py-6"
                        data-testid="kiosk-setup-save"
                    >
                        Save & Lock In
                    </Button>
                    {deviceToken && (
                        <p className="text-[10px] text-[#A0AAB2] mt-4 break-all">
                            Current token: <span className="font-mono">{deviceToken.slice(0, 8)}…{deviceToken.slice(-4)}</span>
                        </p>
                    )}
                </div>
            </div>
        );
    }

    const onAnyInteraction = () => bumpIdle();

    // ────────────────────────── Session reset ──────────────────────────
    const resetSession = () => {
        setStage('idle');
        setPhone('');
        setPin('');
        setPlayer(null);
        setPendingCash(0);
        setError('');
        setLastDeposit(null);
    };

    // ────────────────────────── Sign in (phone + PIN) ──────────────────────────
    const handleIdentify = async () => {
        const digits = phone.replace(/\D/g, '');
        if (digits.length < 7) {
            setError('Enter a valid phone number');
            return;
        }
        if (pin.length !== 4) {
            setError('Enter your 4-digit PIN');
            return;
        }
        setBusy(true);
        setError('');
        try {
            const { data } = await axios.post(
                `${API}/kiosk/identify`,
                { phone, pin },
                { headers: { 'X-Kiosk-Token': deviceToken } },
            );
            setPlayer(data);
            setStage('menu');
        } catch (e) {
            const status = e.response?.status;
            if (status === 401 && e.response?.data?.detail?.includes('kiosk')) {
                // device token bad — force back to setup
                localStorage.removeItem(TOKEN_KEY);
                setDeviceToken('');
                return;
            }
            setError(e.response?.data?.detail || 'Sign-in failed. Try again or see staff.');
        } finally {
            setBusy(false);
        }
    };

    // ────────────────────────── Bill simulator → "I'm done" → submit ──────────────────────────
    const insertBill = (denom) => {
        setPendingCash((c) => c + denom);
        bumpIdle();
    };

    const submitDeposit = async (reason = 'customer-done') => {
        if (!player || pendingCash <= 0) {
            setStage('menu');
            return;
        }
        setBusy(true);
        setError('');
        const txnId = uuid();
        try {
            const { data } = await axios.post(
                `${API}/kiosk/deposit`,
                { user_id: player.user_id, amount: pendingCash, txn_id: txnId },
                { headers: { 'X-Kiosk-Token': deviceToken } },
            );
            setLastDeposit({
                amount: data.amount ?? pendingCash,
                new_balance: data.new_balance,
                deposit_id: data.deposit_id,
                trigger: reason,
            });
            // Fire a receipt if a physical printer is available. Best-effort —
            // never blocks the success screen if the printer is missing/jammed.
            try {
                const tsCT = new Date().toLocaleString('en-US', {
                    timeZone: 'America/Chicago',
                    month: 'short', day: 'numeric', year: 'numeric',
                    hour: 'numeric', minute: '2-digit',
                });
                helperSend({
                    cmd: 'print',
                    lines: [
                        'BETohalics',
                        '',
                        `Player: ${player?.name || ''}`,
                        `Loaded:  $${(data.amount ?? pendingCash).toFixed(2)}`,
                        `Balance: $${(data.new_balance ?? 0).toFixed(2)}`,
                        '',
                        tsCT + ' CT',
                        `Ref: ${data.deposit_id || txnId}`,
                        '',
                        'Thank you!',
                    ],
                });
            } catch { /* never block success screen on printer */ }
            setPlayer((p) => p ? { ...p, credits: data.new_balance } : p);
            setPendingCash(0);
            setStage('success');
        } catch (e) {
            setError(e.response?.data?.detail || 'Deposit failed — please see staff. Your cash will be returned.');
            setStage('error');
        } finally {
            setBusy(false);
        }
    };

    const cancelInsert = () => {
        // In real hardware mode this would tell the helper to RETURN the
        // staged bills back to the customer. The simulator just zeroes them.
        if (pendingCash > 0 && !window.confirm(`Return $${pendingCash.toFixed(2)} to customer?`)) return;
        setPendingCash(0);
        setStage('menu');
    };

    // ────────────────────────── Render: idle screen ──────────────────────────
    if (stage === 'idle') {
        return (
            <div
                className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-6 text-center cursor-pointer select-none"
                onClick={() => setStage('login')}
                onTouchStart={() => setStage('login')}
                data-testid="kiosk-idle-screen"
            >
                <motion.div
                    animate={{ scale: [1, 1.04, 1] }}
                    transition={{ duration: 2.4, repeat: Infinity }}
                >
                    <h1 className="text-5xl sm:text-6xl font-black text-[#FFD700] mb-4" style={{ textShadow: '0 0 30px rgba(255,215,0,0.4)' }}>
                        BETohalics
                    </h1>
                </motion.div>
                <p className="text-2xl text-white mt-2">Tap anywhere to begin</p>
                <p className="text-[#A0AAB2] mt-6 text-sm max-w-md">
                    Sign in with your phone & 4-digit Kiosk PIN to load cash, place bets, or open Slots.
                </p>
            </div>
        );
    }

    // ────────────────────────── Render: shared frame ──────────────────────────
    const FRAME = (children) => (
        <div
            className="min-h-screen bg-[#050505] flex flex-col"
            onTouchStart={onAnyInteraction}
            onClick={onAnyInteraction}
            data-testid="kiosk-frame"
        >
            <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-black text-[#FFD700]">BETohalics Kiosk</h1>
                    {helperStatus ? (
                        <span
                            className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                                helperStatus.acceptor === 'online'
                                    ? 'bg-[#39FF14]/20 text-[#39FF14] border border-[#39FF14]/40'
                                    : 'bg-[#FF8C00]/20 text-[#FF8C00] border border-[#FF8C00]/40'
                            }`}
                            data-testid="kiosk-helper-badge"
                            title={`Acceptor: ${helperStatus.acceptor} · Printer: ${helperStatus.printer}`}
                        >
                            <Wifi className="w-3 h-3" /> Helper · {helperStatus.acceptor}
                        </span>
                    ) : (
                        <span
                            className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/5 text-[#A0AAB2] border border-white/10"
                            data-testid="kiosk-helper-badge"
                            title="Bill-acceptor helper not detected — using on-screen simulator"
                        >
                            <WifiOff className="w-3 h-3" /> Simulator
                        </span>
                    )}
                </div>
                {stage !== 'login' && (
                    <Button
                        onClick={resetSession}
                        variant="outline"
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                        data-testid="kiosk-end-session-btn"
                    >
                        <LogOut className="w-4 h-4 mr-1.5" /> End
                    </Button>
                )}
            </div>
            <div className="flex-1 p-6 max-w-2xl w-full mx-auto">{children}</div>
        </div>
    );

    // ────────────────────────── Render: login (phone + PIN) ──────────────────────────
    if (stage === 'login') {
        return FRAME(
            <div className="space-y-6 mt-8">
                <h2 className="text-3xl font-black text-white text-center">Sign in</h2>
                <div className="space-y-2">
                    <label className="text-white text-base block">Phone Number</label>
                    <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-[#A0AAB2]" />
                        <input
                            type="tel"
                            inputMode="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="(555) 123-4567"
                            className="w-full pl-14 pr-4 py-6 text-2xl bg-[#121212] border border-white/10 rounded-lg text-white placeholder:text-[#A0AAB2]/40 focus:border-[#FFD700] focus:outline-none"
                            autoFocus
                            data-testid="kiosk-phone-input"
                        />
                    </div>
                </div>
                <div className="space-y-2">
                    <label className="text-white text-base block">4-digit PIN</label>
                    <div className="relative">
                        <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 text-[#A0AAB2]" />
                        <input
                            type="password"
                            inputMode="numeric"
                            maxLength={4}
                            value={pin}
                            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                            placeholder="••••"
                            className="w-full pl-14 pr-4 py-6 text-3xl tracking-[0.5em] text-center bg-[#121212] border border-white/10 rounded-lg text-white placeholder:text-[#A0AAB2]/40 focus:border-[#FFD700] focus:outline-none"
                            data-testid="kiosk-pin-input"
                        />
                    </div>
                    <p className="text-xs text-[#A0AAB2]">Don't have a PIN? Set one in the BETohalics app, then come back.</p>
                </div>
                {error && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="kiosk-login-error">
                        <AlertTriangle className="inline w-4 h-4 mr-1" />{error}
                    </div>
                )}
                <div className="flex gap-3">
                    <Button
                        onClick={resetSession}
                        variant="outline"
                        className="border-white/20 text-white hover:bg-white/5 py-6 text-base flex-shrink-0"
                        data-testid="kiosk-login-cancel"
                    >
                        <ArrowLeft className="w-5 h-5 mr-1" /> Cancel
                    </Button>
                    <Button
                        onClick={handleIdentify}
                        disabled={busy}
                        className="flex-1 bg-[#FFD700] text-black hover:bg-[#F4C430] font-bold py-6 text-xl"
                        data-testid="kiosk-login-submit"
                    >
                        {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : 'Sign In'}
                    </Button>
                </div>
            </div>
        );
    }

    // ────────────────────────── Render: post-login menu ──────────────────────────
    if (stage === 'menu') {
        return FRAME(
            <div className="space-y-6 mt-2">
                <div className="text-center">
                    <p className="text-[#A0AAB2] text-sm uppercase tracking-wider">Welcome back</p>
                    <h2 className="text-4xl font-black text-white mt-1" data-testid="kiosk-greeting">{player?.name}</h2>
                    <div className="mt-4 p-5 rounded-xl bg-gradient-to-br from-[#FFD700]/10 to-[#FF007F]/10 border border-[#FFD700]/30">
                        <p className="text-[#A0AAB2] text-xs uppercase">Wallet Balance</p>
                        <p className="text-5xl font-mono font-black text-[#FFD700]" data-testid="kiosk-balance">
                            ${player?.credits?.toFixed(2) ?? '0.00'}
                        </p>
                    </div>
                </div>

                <Button
                    onClick={() => { setStage('loading-cash'); setPendingCash(0); }}
                    className="w-full py-8 text-2xl bg-[#39FF14] text-black hover:bg-[#39FF14]/80 font-black"
                    data-testid="kiosk-load-cash-btn"
                >
                    <Banknote className="w-7 h-7 mr-3" /> Load Cash
                </Button>

                <p className="text-center text-[#A0AAB2] text-sm">
                    After loading, end your session and use any device to play.
                </p>
            </div>
        );
    }

    // ────────────────────────── Render: loading cash (simulator) ──────────────────────────
    if (stage === 'loading-cash') {
        return FRAME(
            <div className="space-y-6 mt-2">
                <div className="text-center">
                    <p className="text-[#A0AAB2] text-sm uppercase tracking-wider">Insert cash below</p>
                    <div className="mt-4 p-6 rounded-xl bg-[#0a0a0a] border-2 border-dashed border-[#FFD700]/40">
                        <p className="text-[#A0AAB2] text-xs uppercase">Inserted this session</p>
                        <p className="text-6xl font-mono font-black text-[#39FF14] mt-2" data-testid="kiosk-pending-amount">
                            ${pendingCash.toFixed(2)}
                        </p>
                        <p className="text-[#A0AAB2] text-xs mt-2">
                            New balance after deposit: <span className="text-white">${((player?.credits || 0) + pendingCash).toFixed(2)}</span>
                        </p>
                    </div>
                </div>

                {/* When real hardware is online, show a friendly prompt.
                    When no helper is detected, fall back to the simulator panel. */}
                {helperStatus?.acceptor === 'online' ? (
                    <div className="p-6 rounded-lg bg-[#39FF14]/5 border border-[#39FF14]/30 text-center" data-testid="kiosk-hw-prompt">
                        <p className="text-[#39FF14] text-lg font-bold uppercase tracking-wider">
                            Insert bills below
                        </p>
                        <p className="text-[#A0AAB2] text-sm mt-2">
                            Cash will appear above as it's accepted. Tap <span className="text-white font-bold">"I'm done"</span> when you've inserted everything.
                        </p>
                    </div>
                ) : (
                    <div className="p-4 rounded-lg bg-[#FF8C00]/10 border border-[#FF8C00]/30">
                        <p className="text-[#FF8C00] text-xs uppercase font-bold mb-3 text-center">
                            Simulator — tap a bill to fake an insert
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                            {BILLS.map((d) => (
                                <Button
                                    key={d}
                                    onClick={() => insertBill(d)}
                                    className="bg-[#121212] border border-white/10 text-white hover:border-[#39FF14] hover:bg-[#39FF14]/10 py-6 text-lg font-bold"
                                    data-testid={`kiosk-sim-bill-${d}`}
                                >
                                    <DollarSign className="w-5 h-5 mr-1" />{d}
                                </Button>
                            ))}
                        </div>
                    </div>
                )}

                {error && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                        <AlertTriangle className="inline w-4 h-4 mr-1" />{error}
                    </div>
                )}

                <div className="flex gap-3">
                    <Button
                        onClick={cancelInsert}
                        variant="outline"
                        className="flex-1 border-white/20 text-white hover:bg-white/5 py-6"
                        data-testid="kiosk-cash-cancel"
                    >
                        <ArrowLeft className="w-5 h-5 mr-1" /> Cancel
                    </Button>
                    <Button
                        onClick={() => submitDeposit('customer-done')}
                        disabled={busy || pendingCash <= 0}
                        className="flex-1 bg-[#FFD700] text-black hover:bg-[#F4C430] font-black py-6 text-xl"
                        data-testid="kiosk-cash-done"
                    >
                        {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : "I'm done"}
                    </Button>
                </div>
            </div>
        );
    }

    // ────────────────────────── Render: success ──────────────────────────
    if (stage === 'success') {
        return FRAME(
            <AnimatePresence>
                <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="space-y-6 mt-10 text-center"
                    data-testid="kiosk-success-screen"
                >
                    <CheckCircle2 className="w-24 h-24 text-[#39FF14] mx-auto" />
                    <h2 className="text-4xl font-black text-white">Loaded!</h2>
                    <div className="p-5 rounded-xl bg-[#0a0a0a] border border-[#39FF14]/40">
                        <p className="text-[#A0AAB2] text-sm uppercase">Added</p>
                        <p className="text-5xl font-mono font-black text-[#39FF14] mt-1">
                            ${lastDeposit?.amount?.toFixed(2) ?? '0.00'}
                        </p>
                        <p className="text-[#A0AAB2] text-sm uppercase mt-4">New balance</p>
                        <p className="text-4xl font-mono font-black text-[#FFD700] mt-1">
                            ${lastDeposit?.new_balance?.toFixed(2) ?? '0.00'}
                        </p>
                    </div>
                    {lastDeposit?.trigger === 'idle-timeout' && (
                        <p className="text-[#FF8C00] text-sm">
                            Cash auto-submitted after timeout. See staff for a printed receipt if needed.
                        </p>
                    )}
                    <div className="flex gap-3">
                        <Button
                            onClick={() => setStage('menu')}
                            className="flex-1 bg-[#FFD700] text-black hover:bg-[#F4C430] font-bold py-6"
                            data-testid="kiosk-success-continue"
                        >
                            Continue
                        </Button>
                        <Button
                            onClick={resetSession}
                            variant="outline"
                            className="flex-1 border-white/20 text-white hover:bg-white/5 py-6"
                            data-testid="kiosk-success-done"
                        >
                            All Done
                        </Button>
                    </div>
                </motion.div>
            </AnimatePresence>
        );
    }

    // ────────────────────────── Render: error ──────────────────────────
    if (stage === 'error') {
        return FRAME(
            <div className="space-y-6 mt-10 text-center" data-testid="kiosk-error-screen">
                <AlertTriangle className="w-24 h-24 text-red-500 mx-auto" />
                <h2 className="text-3xl font-black text-white">Something went wrong</h2>
                <p className="text-[#A0AAB2]">{error || 'Please see staff for assistance.'}</p>
                <Button
                    onClick={resetSession}
                    className="w-full bg-[#FFD700] text-black hover:bg-[#F4C430] font-bold py-6"
                    data-testid="kiosk-error-back"
                >
                    Back to Start
                </Button>
            </div>
        );
    }

    return null;
};

export default KioskPage;
